/**
 * SEO meta helper cho M2 v2 Stage A corpus browser route
 * (/tim-kiem + /van-ban/:id).
 *
 * Spec: specs/M2-EXECUTION-PLAN-v2.md §4 P-A5 AC-14.
 * F5 audit (2026-05-21): EXTEND existing v1 helper voi field rename
 * (doc_number -> doc_num) + add BreadcrumbList + Article JSON-LD.
 * VN copy: 100% co dau (per CLAUDE.md VN copy rule).
 */

export type CorpusSearchSeoInput = {
  total?: number;
  query?: string;
};

export type CorpusDocSeoInput = {
  doc_id: string;
  title: string;
  doc_number: string | null;
  doc_type_name: string | null;
  agency: string | null;
  issue_date: string | null;
  eff_from: string | null;
  eff_to?: string | null;
  eff_status_code?: string | null;
  summary: string | null;
};

const SITE = 'https://asklaw.vn';
const DEFAULT_OG_IMAGE = `${SITE}/og-default.png`;

// ---------------------------------------------------------------------------
// /tim-kiem
// ---------------------------------------------------------------------------

export function applyCorpusSearchSeo(input?: CorpusSearchSeoInput): void {
  if (typeof document === 'undefined') return;

  const query = input?.query?.trim() ?? '';
  const total = input?.total;

  const title = query
    ? `Tra cứu: "${clip(query, 60)}" | Asklaw VBPL`
    : 'Tra cứu văn bản pháp luật Việt Nam | Asklaw';

  const description = query
    ? clip(
        `Kết quả tra cứu "${clip(query, 80)}" trong 54.323 văn bản pháp luật Việt Nam (Trung ương). Lọc theo loại văn bản, hiệu lực, năm ban hành, cơ quan ban hành. Miễn phí, không cần đăng nhập.`,
        160,
      )
    : 'Tìm kiếm văn bản quy phạm pháp luật Việt Nam (Trung ương và Địa phương) với bộ lọc theo loại văn bản, hiệu lực, năm ban hành, cơ quan ban hành. Miễn phí, không cần đăng nhập.';

  const canonical = query
    ? `${SITE}/tim-kiem?q=${encodeURIComponent(query)}`
    : `${SITE}/tim-kiem`;

  document.title = title;
  setMeta('description', description);
  setMeta('og:title', title, true);
  setMeta('og:description', description, true);
  setMeta('og:type', 'website', true);
  setMeta('og:url', canonical, true);
  setMeta('og:image', DEFAULT_OG_IMAGE, true);
  setMeta('og:locale', 'vi_VN', true);
  setMeta('og:site_name', 'Asklaw', true);
  setMeta('twitter:card', 'summary_large_image', true);
  setMeta('twitter:title', title, true);
  setMeta('twitter:description', description, true);
  setMeta('robots', 'index, follow, max-snippet:-1, max-image-preview:large');
  setCanonical(canonical);
  clearJsonLdGroup('corpus-detail');

  // WebSite JSON-LD voi SearchAction (sitelinks search box).
  const websiteLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    url: SITE,
    name: 'Asklaw',
    alternateName: 'Asklaw VBPL',
    description: 'Tra cứu văn bản pháp luật Việt Nam - 54.323 VBPL Trung ương',
    inLanguage: 'vi-VN',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE}/tim-kiem?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
  injectJsonLd('corpus-search-jsonld', 'corpus-search', websiteLd);

  // BreadcrumbList Trang chu -> Tra cuu (-> query if present)
  const breadcrumbItems: Array<Record<string, unknown>> = [
    { '@type': 'ListItem', position: 1, name: 'Trang chủ', item: SITE },
    { '@type': 'ListItem', position: 2, name: 'Tra cứu pháp luật', item: `${SITE}/tim-kiem` },
  ];
  if (query) {
    breadcrumbItems.push({
      '@type': 'ListItem',
      position: 3,
      name: clip(query, 60),
      item: canonical,
    });
  }
  injectJsonLd('corpus-search-breadcrumb-jsonld', 'corpus-search', {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems,
  });

  // SearchResultsPage neu co query + total.
  if (query && typeof total === 'number') {
    injectJsonLd('corpus-search-srp-jsonld', 'corpus-search', {
      '@context': 'https://schema.org',
      '@type': 'SearchResultsPage',
      url: canonical,
      name: title,
      description,
      inLanguage: 'vi-VN',
      mainEntity: { '@type': 'ItemList', numberOfItems: total },
    });
  } else {
    removeJsonLd('corpus-search-srp-jsonld');
  }
}

// ---------------------------------------------------------------------------
// /van-ban/:id
// ---------------------------------------------------------------------------

export function applyCorpusDocSeo(doc: CorpusDocSeoInput): void {
  if (typeof document === 'undefined') return;

  const safeTitle = doc.title.trim();
  const docNumber = doc.doc_number?.trim() || null;
  const titleSuffix = docNumber ? ` - ${docNumber}` : '';
  const title = `${safeTitle}${titleSuffix} | Asklaw`;

  const summary = doc.summary?.trim() || '';
  const fallbackDesc = buildFallbackDesc(doc);
  const description = clip(summary || fallbackDesc, 160);
  const canonical = `${SITE}/van-ban/${encodeURIComponent(doc.doc_id)}`;

  document.title = `${safeTitle} - Asklaw`;
  setMeta('description', description);
  setMeta('og:title', title, true);
  setMeta('og:description', description, true);
  setMeta('og:type', 'article', true);
  setMeta('og:url', canonical, true);
  setMeta('og:image', DEFAULT_OG_IMAGE, true);
  setMeta('og:locale', 'vi_VN', true);
  setMeta('og:site_name', 'Asklaw', true);
  setMeta('twitter:card', 'summary_large_image', true);
  setMeta('twitter:title', title, true);
  setMeta('twitter:description', description, true);
  setMeta('robots', 'index, follow, max-snippet:-1, max-image-preview:large');
  if (doc.issue_date) {
    setMeta('article:published_time', doc.issue_date.slice(0, 10), true);
  }
  setCanonical(canonical);

  // BreadcrumbList JSON-LD.
  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Trang chủ', item: SITE },
      { '@type': 'ListItem', position: 2, name: 'Tìm kiếm văn bản', item: `${SITE}/tim-kiem` },
      { '@type': 'ListItem', position: 3, name: safeTitle, item: canonical },
    ],
  };
  injectJsonLd('corpus-doc-breadcrumb-jsonld', 'corpus-detail', breadcrumbLd);

  // Legislation JSON-LD (enriched Q5 SEO 92->95 launch fix).
  const legLd: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Legislation',
    '@id': canonical,
    name: safeTitle,
    headline: safeTitle,
    description,
    url: canonical,
    inLanguage: 'vi-VN',
    isAccessibleForFree: true,
    identifier: docNumber || doc.doc_id,
    legislationIdentifier: docNumber || doc.doc_id,
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
    publisher: {
      '@type': 'Organization',
      name: 'Asklaw',
      url: SITE,
      logo: {
        '@type': 'ImageObject',
        url: `${SITE}/og-default.png`,
      },
    },
    jurisdiction: {
      '@type': 'AdministrativeArea',
      name: 'Vietnam',
      addressCountry: 'VN',
    },
  };
  if (doc.issue_date) {
    legLd.datePublished = doc.issue_date.slice(0, 10);
    legLd.legislationDate = doc.issue_date.slice(0, 10);
  }
  if (doc.eff_from) legLd.legislationDateOfApplicability = doc.eff_from.slice(0, 10);
  if (doc.eff_to) legLd.expires = doc.eff_to.slice(0, 10);
  if (doc.agency) {
    legLd.legislationPassedBy = {
      '@type': 'GovernmentOrganization',
      name: doc.agency,
    };
    legLd.creator = {
      '@type': 'GovernmentOrganization',
      name: doc.agency,
    };
  }
  if (doc.doc_type_name) {
    legLd.legislationType = doc.doc_type_name;
    legLd.genre = doc.doc_type_name;
  }
  if (doc.eff_status_code) {
    legLd.legislationLegalForce = mapLegalForce(doc.eff_status_code);
  }
  injectJsonLd('corpus-doc-legislation-jsonld', 'corpus-detail', legLd);
}

function mapLegalForce(code: string): string {
  switch (code) {
    case 'CHL':
      return 'https://schema.org/InForce';
    case 'HHL':
      return 'https://schema.org/NotInForce';
    case 'HHL1P':
      return 'https://schema.org/PartiallyInForce';
    case 'TNHL':
      return 'https://schema.org/InForce';
    case 'CHHL':
      return 'https://schema.org/NotYetInForce';
    default:
      return 'https://schema.org/InForce';
  }
}

function buildFallbackDesc(doc: CorpusDocSeoInput): string {
  const parts: string[] = [];
  if (doc.doc_type_name) parts.push(doc.doc_type_name);
  if (doc.doc_number) parts.push(`số ${doc.doc_number}`);
  if (doc.agency) parts.push(`do ${doc.agency} ban hành`);
  if (doc.issue_date) parts.push(`ngày ${formatDateVi(doc.issue_date)}`);
  const head = parts.join(' ');
  return head
    ? `${head}. Tra cứu toàn văn tại Asklaw - thư viện văn bản pháp luật Việt Nam.`
    : `${doc.title}. Tra cứu toàn văn tại Asklaw - thư viện văn bản pháp luật Việt Nam.`;
}

function formatDateVi(iso: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  return `${m[3]}/${m[2]}/${m[1]}`;
}

function clip(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, Math.max(0, max - 3)).trimEnd() + '...';
}

// ---------------------------------------------------------------------------
// Internal DOM helpers
// ---------------------------------------------------------------------------

function setMeta(name: string, content: string, isProperty = false): void {
  const attr = isProperty ? 'property' : 'name';
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.content = content;
}

function setCanonical(href: string): void {
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.rel = 'canonical';
    document.head.appendChild(el);
  }
  el.href = href;
}

function injectJsonLd(id: string, group: string, obj: unknown): void {
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    el.dataset.group = group;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(obj);
}

function clearJsonLdGroup(group: string): void {
  const nodes = document.querySelectorAll(
    `script[type="application/ld+json"][data-group="${group}"]`,
  );
  nodes.forEach((n) => n.parentElement?.removeChild(n));
}

function removeJsonLd(id: string): void {
  const el = document.getElementById(id);
  if (el) el.parentElement?.removeChild(el);
}
